import dayjs from 'dayjs'
import { ConnectionType } from '~/constants/connections'
import { Index, IndexStatus } from '~/types/Indexes'
import {
  ConnAccessToken,
  CustomConnectionParam,
  IndexRuntimeSettings,
  RepositoryBranches,
  TestConnection
} from '~/types/ResponseType'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { extractApiErrorMessage } from '~/utils/shared'
import {
  UploadOptions,
  getPresignedUrl,
  notifyUploadComplete,
  uploadWithPresignedUrl
} from '~/utils/presignedUpload'
import { getDeviceId } from '~/utils/storage'
import { Workspace } from '~/utils/workspaceSelection'

export type UploadFileOptions = UploadOptions

/**
 * Sanitize filename for S3 upload.
 * - Replaces all whitespace variants (including non-breaking spaces) with regular spaces
 * - Collapses consecutive periods to prevent path traversal issues
 * - Trims leading/trailing whitespace
 */
export const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/[\s\u00A0]+/g, ' ') // Replace all whitespace variants with regular space
    .replace(/\.{2,}/g, '.') // Collapse consecutive periods
    .trim() // Remove leading/trailing whitespace
}

const normalizeConnTypeForApi = (connType: string) => {
  if (connType === ConnectionType.SelfHostedGithub) {
    return connType.toLowerCase()
  }
  if (connType === 'github_custom') {
    return ConnectionType.SelfHostedGithub.toLowerCase()
  }
  return connType.toLowerCase()
}

/**
 * Generic upload function using workspace-scoped client-type routes
 * Consolidates common upload logic for different client types
 *
 * @param workspaceUuid - The workspace UUID
 * @param clientType - The client type (e.g., 'coding_guidelines', 's3_upload')
 * @param file - The file to upload
 * @param options - Upload options including callbacks
 * @param withVirusScan - Whether to enable virus scanning (default: true)
 */
async function uploadWithClientType(
  workspaceUuid: string,
  clientType: string,
  file: File,
  options: UploadFileOptions,
  withVirusScan: boolean = true
) {
  const { onProgress, onComplete = () => {}, onError = () => {} } = options

  try {
    // Get presigned URL from backend using workspace-scoped client-type route
    const presignedData = await getPresignedUrl(
      `/api/workspace/${workspaceUuid}/org/upload/${clientType}/presigned-url`,
      file.name,
      file.size
    )

    // Upload directly to S3
    await uploadWithPresignedUrl(file, presignedData, onProgress)

    if (withVirusScan) {
      // Notify backend and stream virus scan status
      await notifyUploadComplete(
        `/api/workspace/${workspaceUuid}/org/upload/${clientType}/complete`,
        presignedData.file_path,
        options
      )
    } else {
      // Notify backend without virus scan
      const response = await fetchWrapper(
        `/api/workspace/${workspaceUuid}/org/upload/${clientType}/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ file_path: presignedData.file_path })
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      onComplete(result.path || presignedData.file_path, 'SCAN_SKIPPED')
    }
  } catch (error) {
    onError(error instanceof Error ? error.message : 'An unknown error occurred')
  }
}

/**
 * Upload coding guidelines (PDFs)
 * No virus scanning for PDFs
 */
export async function uploadCodingGuidelines(
  workspaceUuid: string,
  file: File,
  options: UploadFileOptions
) {
  return uploadWithClientType(workspaceUuid, 'coding_guidelines', file, options, false)
}

export const getWorkspaceMembers = async (workspaceUuid: string) => {
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/org/users`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (response.status !== 200) {
    throw new Error('Failed to get workspace members')
  }
  return response.json()
}

export const getAzureDevOpsOrganizations = async (workspaceUuid: string) => {
  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/conn/azure_devops/available-organizations`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )
  if (!response.ok) {
    const error = await response.text()
    throw new Error('Failed to get available organizations. Reason: ' + error)
  }
  return await response.json()
}

export const setAzureDevOpsOrganization = async (org_name: string, workspaceUuid: string) => {
  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/conn/azure_devops/set-organization?org_name=${encodeURIComponent(org_name)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )
  if (!response.ok) {
    const error = await response.text()
    throw new Error('Failed to set organization. Reason: ' + error)
  }
  return await response.json()
}

export const getReposWithBranches = async (conn_type: string, workspaceUuid: string) => {
  conn_type = conn_type.toLowerCase()
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/conn/${conn_type}/repos`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error('Failed to get repositories. Reason: ' + error)
  }
  const reposWithBranches = await response.json()
  // Format: [{ name: string, branches: string[], default_branch?: string | null }]
  return reposWithBranches as Array<{
    name: string
    branches: string[]
    default_branch?: string | null
  }>
}

export const getRepoBranches = async (
  conn_type: string,
  full_name: string,
  page: number,
  workspaceUuid: string,
  search: string = '',
  cursor: string = '',
  options: { signal?: AbortSignal } = {}
) => {
  const params = new URLSearchParams({
    full_name,
    page: page.toString(),
    search,
    cursor
  }).toString()
  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/conn/${conn_type}/branches?${params}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      signal: options.signal
    }
  )
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  const data = await response.json()

  return data
}

export const updateOrgRepos = async (
  conn_type: string,
  repos: RepositoryBranches[],
  workspaceUuid: string
) => {
  conn_type = conn_type.toLowerCase()
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/conn/${conn_type}/repos`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(repos)
  })
  if (!response.ok) {
    throw new Error('Failed to update authorized branch')
  }
}

export const updateOnboardStarted = async (workspaceUuid: string, onboardStarted: boolean) => {
  const path = `/api/workspace/${workspaceUuid}/org/onboard-started`
  const response = await fetchWrapper(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ onboard_started: onboardStarted })
  })
  if (!response.ok) {
    throw new Error('Failed to update onboard started')
  }
  return response.json()
}

export const getIndexes = async (workspaceUuid: string) => {
  const params = new URLSearchParams({
    device_id: window.getDeviceId()
  }).toString()
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/org/index?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
  const data: Index[] = await response.json()
  return data
}

export const updateIndexStatus = async (
  workspaceUuid: string,
  index: { id: number; status: IndexStatus }
) => {
  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/indexes/${index.id}/update`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status: index.status })
    }
  )
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }
}

export const getBillingAccountWorkspaces = async (
  billingAccountId: number
): Promise<Workspace[]> => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/workspaces`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    throw new Error('Failed to get billing account workspaces')
  }
  return await response.json()
}

// Clean billing-level invoice function
export const getBillingAccountInvoices = async (billingAccountId: number, limit: number = 10) => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/invoices?limit=${limit}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    throw new Error('Failed to get billing account invoices')
  }
  const data = await response.json()
  return data
}

// New billing-level workspace creation
export const createWorkspace = async (billingAccountId: number, workspaceName: string) => {
  const response = await fetchWrapper(`/api/billing/${billingAccountId}/workspaces`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ workspace_name: workspaceName })
  })
  return response.json()
}

export const changeOrgName = async (payload: { workspaceUuid: string; org_name: string }) => {
  const { workspaceUuid, org_name } = payload
  const path = `/api/workspace/${workspaceUuid}/org/update-org-name`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ org_name: org_name })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update organization name. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const changeSSOClientID = async (payload: {
  workspaceUuid: string
  client_id: string
  callback_url: string
}) => {
  const { workspaceUuid, client_id, callback_url } = payload
  const path = `/api/workspace/${workspaceUuid}/org/update-org-sso-clientID`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ client_id: client_id, callback_url: callback_url })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update SSO Client ID. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const changeSSOClientSecret = async (payload: {
  workspaceUuid: string
  client_secret: string
  callback_url: string
}) => {
  const { workspaceUuid, client_secret, callback_url } = payload
  const path = `/api/workspace/${workspaceUuid}/org/update-org-sso-client-secret`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ client_secret: client_secret, callback_url: callback_url })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update SSO Client Secret. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const changeSSOTenantID = async (payload: {
  workspaceUuid: string
  client_tenantid: string
  callback_url: string
}) => {
  const { workspaceUuid, client_tenantid, callback_url } = payload
  const path = `/api/workspace/${workspaceUuid}/org/update-org-sso-tenant-id`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ client_tenantid: client_tenantid, callback_url: callback_url })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update SSO Tenant ID. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const changeSSOCallBackUrl = async (payload: {
  workspaceUuid: string
  callback_url: string
}) => {
  const { workspaceUuid, callback_url } = payload
  const path = `/api/workspace/${workspaceUuid}/org/update-org-sso-call-back-url`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ callback_url: callback_url })
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update SSO Callback Url. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const updateCustomConnection = async (
  workspaceUuid: string,
  payload: CustomConnectionParam
) => {
  const apiConnType = normalizeConnTypeForApi(payload.connType)
  const reqParam: Record<string, unknown> = {
    conn_type: apiConnType,
    client_id: payload.clientId,
    client_secret: payload.clientSecret,
    server_url: payload.serverUrl,
    redirect_url: payload.redirectUrl,
    api_key: payload.apiKey,
    app_name: payload.appName,
    verify_ssl: payload.verifySsl,
    app_id: payload.appId,
    private_key: payload.privateKey
  }
  if (payload.apiUrl) {
    reqParam.api_url = payload.apiUrl
  }
  if (payload.serverCaCertBase64) {
    reqParam.server_ca_cert_base64 = payload.serverCaCertBase64
  } else if (!payload.hasServerCaCert) {
    reqParam.server_ca_cert_base64 = null
  }
  const path = `/api/workspace/${workspaceUuid}/conn/update-connection`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(reqParam)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update custom connection. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const setConnAccessToken = async (workspaceUuid: string, accessToken: ConnAccessToken) => {
  const reqParam = {
    token: accessToken.token
  }
  const connType = normalizeConnTypeForApi(accessToken.connType)
  const path = `/api/workspace/${workspaceUuid}/conn/${connType}/set-access-token`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(reqParam)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to set connection access token. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const testConnection = async (workspaceUuid: string, testConnection: TestConnection) => {
  const reqParam = {
    token: testConnection.token,
    server_url: testConnection.serverUrl
  }
  const conn_type = normalizeConnTypeForApi(testConnection.conn_type)
  const path = `/api/workspace/${workspaceUuid}/conn/${conn_type}/validate-token`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(reqParam)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to test connection. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const getConnectionMetadataList = async (workspaceUuid: string) => {
  const path = `/api/workspace/${workspaceUuid}/conn/get-connection-metadata`
  const response = await fetchWrapper(path, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get connection metadata list. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const getConnectionStatus = async (workspaceUuid: string) => {
  const path = `/api/workspace/${workspaceUuid}/conn/status`
  const response = await fetchWrapper(path, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get connection status. Reason: ${reason}`)
  }
  const data = await response.json()
  return data as { connections: string[]; connected_connections: string[] }
}

export const getOrganizationPlan = async () => {
  const path = `/api/org/plan`
  const response = await fetchWrapper(path, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    throw new Error('Failed to get user organizations')
  }
  const data = await response.json()
  return data
}

export const changeSessionTimeOut = async (workspaceUuid: string, timeout: number) => {
  const reqParam = {
    timeout: timeout
  }
  const path = `/api/workspace/${workspaceUuid}/org/set-timeout`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(reqParam)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to set session timeout. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const getSelectedBranches = async (conn_type: string, workspaceUuid: string) => {
  const path = `/api/workspace/${workspaceUuid}/conn/${conn_type}/selected-branches`
  const response = await fetchWrapper(path, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get updated branch. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

// Organization-level runtime settings: Java/Maven/Mule versions + settings.xml.
// The backend merges only the fields provided, so callers can send a single
// version field or the full XML payload without clobbering the others.
export const changeXmlSettings = async (
  workspaceUuid: string,
  xmlSettings: {
    java_version?: string
    maven_version?: string
    mule_runtime_version?: string
    settings_xml_raw_txt?: string
    settings_security_xml_raw_txt?: string
    settings_xml_file_name?: string
    settings_security_xml_file_name?: string
  }
) => {
  const path = `/api/workspace/${workspaceUuid}/org/update-xml-settings`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(xmlSettings)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update XML settings. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const changeIndexRuntimeSetting = async (payload: {
  workspaceUuid: string
  index_id: number
  runtimeSetting: IndexRuntimeSettings
}) => {
  const { workspaceUuid, index_id, runtimeSetting } = payload
  const path = `/api/workspace/${workspaceUuid}/indexes/${index_id}/update-runtime-setting`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(runtimeSetting)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update index runtime setting. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const getCustomConnectionConfig = async (workspaceUuid: string, conn_type: string) => {
  const isSelfHostedGithub =
    conn_type === ConnectionType.SelfHostedGithub || conn_type === 'github_custom'
  const isSelfHostedGitlab = conn_type === ConnectionType.SelfHostedGitlab
  const apiConnType = normalizeConnTypeForApi(conn_type)
  const path = `/api/workspace/${workspaceUuid}/conn/${apiConnType}/get-custom-connection-config`
  const response = await fetchWrapper(path, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    suppressErrorToast: true
  })
  if (response.status === 404) {
    return {
      conn_type: isSelfHostedGithub ? ConnectionType.SelfHostedGithub : conn_type,
      server_url: '',
      client_id: '',
      client_secret: '',
      redirect_uri: '',
      api_key: '',
      app_name: '',
      verify_ssl: true,
      app_id: '',
      has_private_key: false,
      has_server_ca_cert: false,
      is_self_hosted: isSelfHostedGithub || isSelfHostedGitlab
    }
  }
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to get custom connection config. Reason: ${reason}`)
  }
  const data = await response.json()
  return data
}

export const unlockUserAccount = async (email: string) => {
  const response = await fetchWrapper('/api/v1/user/unlock-account', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ emailToUnlock: email })
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }

  return response.json()
}

export interface OrgRepo {
  filename: string
  path: string
  uploaded_at: string
}

export async function listOrgRepos(workspaceUuid: string): Promise<{ repositories: OrgRepo[] }> {
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/org/org-repos`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }

  return response.json()
}

export async function deleteOrgRepo(workspaceUuid: string, filename: string) {
  const params = new URLSearchParams({ filename }).toString()
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/org/org-repos?${params}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(error)
  }

  return response.json()
}

export interface DeviceRepo {
  filename: string
  path: string
  uploaded_at: string
}

// Workspace-aware user role update
export const updateUserRole = async (workspaceUuid: string, userId: number, role: string) => {
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/org/update-org-user-role`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      user_id: userId,
      role: role
    })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Update user role failed'))
  }

  return response.json()
}

// Workspace-aware user removal
export const removeOrgUser = async (workspaceUuid: string, userId: number) => {
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/org/remove-user`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      user_id: userId
    })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Remove user from workspace failed'))
  }

  return response.json()
}

/**
 * Upload file for workspace with virus scanning
 */
export async function uploadFile(workspaceUuid: string, file: File, options: UploadFileOptions) {
  return uploadWithClientType(workspaceUuid, 's3_upload', file, options, true)
}

/**
 * Upload organization repository (same as uploadFile)
 */
export async function uploadOrgRepo(workspaceUuid: string, file: File, options: UploadFileOptions) {
  return uploadWithClientType(workspaceUuid, 's3_upload', file, options, true)
}

/**
 * Get presigned download URL for a context upload file.
 * Used for downloading files attached to previous messages.
 */
export async function getContextUploadDownloadUrl(
  workspaceUuid: string,
  s3Path: string
): Promise<{ url: string; filename: string }> {
  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/upload/context-download-url`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ s3_path: s3Path })
    }
  )

  if (!response.ok) {
    const error = await response.json()
    throw new Error(extractApiErrorMessage(error, 'Failed to get download URL'))
  }

  return response.json()
}

/**
 * Upload context files (documents, images, code, videos, etc.)
 * No virus scanning for these files.
 * Filename is transformed with device ID and timestamp pattern: {clientId}-{timestamp}___{originalname}
 * Supported extensions are defined in useContextUpload.ts (ALLOWED_CONTEXT_UPLOAD_EXTENSIONS)
 */
export async function uploadMetadataFile(
  workspaceUuid: string,
  file: File,
  options: UploadFileOptions
) {
  // Generate filename with device ID and timestamp pattern
  const clientId = getDeviceId()
  const timestamp = dayjs().format('YYYYMMDDHHmmss')
  // Sanitize original filename to handle non-ASCII whitespace (e.g., macOS non-breaking spaces)
  const sanitizedOriginalName = sanitizeFilename(file.name)
  const transformedFilename = `${clientId}-${timestamp}___${sanitizedOriginalName}`

  // Create new File object with transformed filename
  const fileWithPattern = new File([file], transformedFilename, { type: file.type })

  return uploadWithClientType(
    workspaceUuid,
    'extra_uploaded_context',
    fileWithPattern,
    options,
    false
  )
}

export const deleteWorkspace = async (workspaceUuid: string) => {
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/org/delete-org`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  return response.json()
}

export const deleteConnection = async (conn_type: string, workspaceUuid: string) => {
  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/conn/${conn_type.toLowerCase()}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      // The disconnect UI shows its own classified error toast (e.g. expired AWS
      // session → "run aws sso login"). Suppress the generic global error toast
      // so the user doesn't get a second, raw, conflicting message.
      suppressErrorToast: true
    }
  )
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error || 'Failed to delete connection')
  }
  return response.json()
}

// Coding Guidelines API functions
export interface CodingGuidelinesMetadata {
  s3_path: string
  filename: string
  file_size: number
  uploaded_at: string
}

export interface CodingGuidelinesResponse {
  coding_guidelines: CodingGuidelinesMetadata | null
}

export async function getCodingGuidelines(
  workspaceUuid: string
): Promise<CodingGuidelinesResponse> {
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/org/coding-guidelines`)
  return response.json()
}

export async function updateCodingGuidelinesMetadata(
  workspaceUuid: string,
  s3_path: string,
  filename: string,
  file_size: number
): Promise<{ status: string; coding_guidelines: CodingGuidelinesMetadata }> {
  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/org/update-coding-guidelines-metadata`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        s3_path,
        filename,
        file_size
      })
    }
  )
  return response.json()
}

export async function deleteCodingGuidelines(
  workspaceUuid: string
): Promise<{ status: string; message: string }> {
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/org/coding-guidelines`, {
    method: 'DELETE'
  })
  return response.json()
}

// Enhanced uploadCodingGuidelines with metadata handling
export async function uploadCodingGuidelinesWithMetadata(
  workspaceUuid: string,
  file: File,
  options: UploadFileOptions
) {
  const {
    onProgress = () => {},
    onComplete = () => {},
    onError = () => {},
    onVirusScanStatus = () => {}
  } = options

  try {
    // First upload the file using the existing endpoint
    await uploadCodingGuidelines(workspaceUuid, file, {
      onProgress,
      onComplete: async (uploadedPath: string, virusScanStatus?: string) => {
        try {
          // Then update the metadata
          await updateCodingGuidelinesMetadata(workspaceUuid, uploadedPath, file.name, file.size)
          onComplete(uploadedPath, virusScanStatus)
        } catch {
          onError('Failed to update coding guidelines metadata')
        }
      },
      onError,
      onVirusScanStatus
    })
  } catch (error) {
    onError(error instanceof Error ? error.message : 'Upload failed')
  }
}
